import { useState } from 'react';
import {
  Badge,
} from "@geely/ui";
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import type { Appointment, AppointmentStatus } from '@geely/shared';
import type { PaginationMeta } from '@geely/shared/types';
import { formatDate, formatTime } from '@/lib/utils';
import { AppointmentDetailDialog } from '@/app/pages/admin/dashboard/components/AppointmentDetailDialog';
import { cn } from '@geely/ui';

const STATUS_STYLE: Record<AppointmentStatus, { bg: string; color: string; border: string; label: string }> = {
  pending:          { bg: '#fef9c3', color: '#854d0e', border: '#fde68a', label: 'Pendente' },
  in_progress:      { bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe', label: 'Em andamento' },
  completed:        { bg: '#dcfce7', color: '#166534', border: '#bbf7d0', label: 'Concluído' },
  cancelled:        { bg: '#fee2e2', color: '#991b1b', border: '#fecaca', label: 'Cancelado' },
  customer_absent:  { bg: '#f4f4f5', color: '#3f3f46', border: '#e4e4e7', label: 'Cliente ausente' },
};

function StatusBadgeSelect({
  value,
  statuses,
  onChange,
}: {
  value: AppointmentStatus;
  statuses: { value: AppointmentStatus; label: string }[];
  onChange: (v: AppointmentStatus) => void;
}) {
  const s = STATUS_STYLE[value] ?? STATUS_STYLE.pending;
  return (
    <div className="relative inline-flex items-center">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as AppointmentStatus)}
        style={{
          paddingTop: 4,
          paddingBottom: 4,
          paddingLeft: 9,
          paddingRight: 26,
          borderRadius: 20,
          fontSize: 11,
          fontWeight: 600,
          fontFamily: 'inherit',
          border: `1.5px solid ${s.border}`,
          backgroundColor: s.bg,
          color: s.color,
          appearance: 'none',
          cursor: 'pointer',
          outline: 'none',
        }}
      >
        {statuses.map((st) => (
          <option key={st.value} value={st.value} style={{ background: '#fff', color: '#18181b' }}>
            {st.label}
          </option>
        ))}
      </select>
      <svg
        width="10" height="10" viewBox="0 0 24 24" fill="none"
        stroke={s.color} strokeWidth="2.5" strokeLinecap="round"
        style={{ position: 'absolute', right: 8, pointerEvents: 'none' }}
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </div>
  );
}

interface AppointmentsTableProps {
  appointments: Appointment[];
  statuses: { value: AppointmentStatus; label: string }[];
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSortChange: (column: string) => void;
  onStatusChange: (appointmentId: string, newStatus: AppointmentStatus) => void;
  meta?: PaginationMeta;
  onPageChange?: (page: number) => void;
  limit?: number;
  onLimitChange?: (limit: number) => void;
}

interface SortableHeadProps {
  column: string;
  label: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSortChange: (column: string) => void;
}

function SortableHead({ column, label, sortBy, sortOrder, onSortChange }: SortableHeadProps) {
  const isActive = sortBy === column;
  return (
    <th
      className="px-4 py-[9px] text-left text-[11px] font-bold text-[#52525b] border-b border-[#e8e8e8] bg-[#f7f8fa] uppercase tracking-[0.04em] whitespace-nowrap cursor-pointer select-none hover:bg-[#eef2fb] transition-colors"
      onClick={() => onSortChange(column)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive ? (
          sortOrder === 'asc'
            ? <ChevronUp className="size-[10px] text-[#2563eb]" />
            : <ChevronDown className="size-[10px] text-[#2563eb]" />
        ) : (
          <ChevronsUpDown className="size-[10px] text-[#c0c0c0]" />
        )}
      </span>
    </th>
  );
}

function PlainHead({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-[9px] text-left text-[11px] font-bold text-[#52525b] border-b border-[#e8e8e8] bg-[#f7f8fa] uppercase tracking-[0.04em] whitespace-nowrap">
      {children}
    </th>
  );
}

function PageBtn({
  children,
  active,
  disabled,
  onClick,
  wide,
}: {
  children: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  wide?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex items-center justify-center gap-1 h-8 border rounded-md text-[12px] font-medium transition-colors outline-none',
        wide ? 'px-3' : 'w-8',
        active
          ? 'bg-[#2563eb] border-[#2563eb] text-white font-semibold'
          : disabled
          ? 'border-[#e4e4e7] text-[#d4d4d8] cursor-not-allowed bg-white'
          : 'border-[#e4e4e7] text-[#52525b] bg-white hover:bg-[#f4f4f5] cursor-pointer',
      )}
    >
      {children}
    </button>
  );
}

function PaginationFooter({
  meta,
  onPageChange,
  limit,
  onLimitChange,
}: {
  meta: PaginationMeta;
  onPageChange: (page: number) => void;
  limit: number;
  onLimitChange: (limit: number) => void;
}) {
  const { page, total, totalPages } = meta;
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  const goTo = (n: number) => onPageChange(Math.max(1, Math.min(totalPages, n)));

  const pageNums = (() => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 3) return [1, 2, 3, 4, 5];
    if (page >= totalPages - 2) return Array.from({ length: 5 }, (_, i) => totalPages - 4 + i);
    return [page - 2, page - 1, page, page + 1, page + 2];
  })();

  return (
    <div className="flex items-center px-5 py-3 border-t border-[#f0f0f0] bg-[#f7f8fa] gap-4">
      {/* Per-page + count */}
      <div className="flex items-center gap-[7px] flex-1 text-[12px] text-[#71717a]">
        <span>Mostrar</span>
        <select
          value={limit}
          onChange={(e) => { onLimitChange(Number(e.target.value)); onPageChange(1); }}
          className="px-2 pr-[22px] py-1 border border-[#e4e4e7] rounded-md text-[12px] text-[#18181b] bg-white outline-none cursor-pointer appearance-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%2371717a' stroke-width='2.5' stroke-linecap='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 5px center',
          }}
        >
          <option value={10}>10</option>
          <option value={30}>30</option>
          <option value={50}>50</option>
        </select>
        <span>por página</span>
        <span className="w-px h-[14px] bg-[#d4d4d8] mx-1.5 shrink-0" />
        <span className="text-[#a1a1aa]">{start}–{end} de {total} resultados</span>
      </div>

      {/* Page controls */}
      <div className="flex items-center gap-1">
        <PageBtn wide disabled={page === 1} onClick={() => goTo(page - 1)}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="m15 18-6-6 6-6" />
          </svg>
          Anterior
        </PageBtn>
        {pageNums.map((n) => (
          <PageBtn key={n} active={n === page} onClick={() => goTo(n)}>{n}</PageBtn>
        ))}
        <PageBtn wide disabled={page === totalPages} onClick={() => goTo(page + 1)}>
          Próximo
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="m9 18 6-6-6-6" />
          </svg>
        </PageBtn>
      </div>
    </div>
  );
}

export function AppointmentsTable({
  appointments,
  statuses,
  sortBy,
  sortOrder,
  onSortChange,
  onStatusChange,
  meta,
  onPageChange,
  limit = 10,
  onLimitChange,
}: AppointmentsTableProps) {
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);

  const sortProps = { sortBy, sortOrder, onSortChange };

  return (
    <>
      <div className="bg-white border border-[#e4e4e7] rounded-xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] overflow-hidden">

        {/* Header */}
        <div className="px-5 py-[15px] border-b border-[#f0f0f0]">
          <span className="text-[15px] font-bold text-[#18181b]">Agendamentos</span>
          <span className="text-[13px] text-[#a1a1aa] ml-[7px] font-normal">({appointments.length})</span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <SortableHead column="date" label="Data" {...sortProps} />
                <SortableHead column="time" label="Horário" {...sortProps} />
                <SortableHead column="customerName" label="Cliente" {...sortProps} />
                <PlainHead>Modelo/Versão</PlainHead>
                <SortableHead column="customerPhone" label="Telefone" {...sortProps} />
                <SortableHead column="customerEmail" label="Email" {...sortProps} />
                <SortableHead column="confirmedAt" label="Confirmação" {...sortProps} />
                <SortableHead column="status" label="Status" {...sortProps} />
              </tr>
            </thead>
            <tbody>
              {appointments.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-[13px] text-[#a1a1aa]">
                    Nenhum agendamento encontrado
                  </td>
                </tr>
              ) : (
                appointments.map((apt, i) => (
                  <tr
                    key={apt.id}
                    className={cn(
                      'cursor-pointer transition-all duration-75',
                      'hover:bg-[#f0f6ff] hover:shadow-[inset_3px_0_0_#2563eb]',
                      i % 2 !== 0 ? 'bg-[#fafafa]' : 'bg-white',
                      i < appointments.length - 1 && (i % 2 !== 0 ? 'border-b border-[#f0f0f0]' : 'border-b border-[#f5f5f5]'),
                    )}
                    onClick={() => setSelectedAppointment(apt)}
                  >
                    <td className="px-4 py-[13px] text-[13px] text-[#18181b] whitespace-nowrap">{formatDate(apt.date)}</td>
                    <td className="px-4 py-[13px] text-[13px] text-[#52525b]">{formatTime(apt.time)}</td>
                    <td className="px-4 py-[13px] text-[13px] font-semibold text-[#18181b] whitespace-nowrap">{apt.customerName}</td>
                    <td className="px-4 py-[13px] text-[13px] whitespace-nowrap">
                      <span className="font-medium">{apt.model?.name ?? '—'}</span>
                      {apt.version?.name && (
                        <span className="text-[#a1a1aa] font-normal"> / {apt.version.name}</span>
                      )}
                    </td>
                    <td className="px-4 py-[13px] text-[13px] text-[#71717a] whitespace-nowrap">{apt.customerPhone}</td>
                    <td className="px-4 py-[13px] text-[13px] text-[#71717a] max-w-[200px] truncate">{apt.customerEmail}</td>
                    <td className="px-4 py-[13px]">
                      {apt.confirmedAt ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          ✓ Confirmado
                        </Badge>
                      ) : apt.status === 'pending' ? (
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                          Pendente
                        </Badge>
                      ) : (
                        <span className="text-[#d4d4d8]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-[13px]" onClick={(e) => e.stopPropagation()}>
                      <StatusBadgeSelect
                        value={apt.status}
                        statuses={statuses}
                        onChange={(v) => onStatusChange(apt.id, v)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        {meta && onPageChange && onLimitChange && (
          <PaginationFooter
            meta={meta}
            onPageChange={onPageChange}
            limit={limit}
            onLimitChange={onLimitChange}
          />
        )}
      </div>

      <AppointmentDetailDialog
        appointment={selectedAppointment}
        isOpen={!!selectedAppointment}
        onClose={() => setSelectedAppointment(null)}
      />
    </>
  );
}
